import React from 'react';
import { 
  Activity, Laptop, CheckCircle2, ShieldCheck, 
  Zap, BarChart3, Database, HeartPulse, 
  Stethoscope, Brain, Bone, Baby, ChevronRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import FadeIn from '../components/FadeIn';

// Asset Imports
import billingImg from '../assets/images/ABS2GSk8XC5WyadcrjvH97lJgSAPcLbi.png';
import itImg from '../assets/images/360_F_1688446586_JI7MuSv3PZyGn6p.png';

const Services = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      
      {/* 1. HERO SECTION: SOLUTION-FOCUSED */}
      <section className="bg-slate-900 py-24 px-8 text-center text-white relative overflow-hidden">
        <motion.div 
          animate={{ opacity: [0.05, 0.1, 0.05], scale: [1, 1.1, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute inset-0 bg-[#14b8a6] rounded-full blur-[150px] -top-1/2 left-1/4"
        />
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeIn direction="down">
            <h1 className="text-4xl md:text-7xl font-black mb-8 leading-tight">
              Integrated <span className="text-[#14b8a6]">Healthcare</span> Solutions.
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
              We merge established Revenue Cycle Management with specialized IT solutions 
              to modernize your practice and maximize financial health.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 2. SPECIALTY-SPECIFIC EXPERTISE (New Requested Section) */}
      <section className="py-24 px-8 max-w-7xl mx-auto">
        <FadeIn direction="up">
          <div className="text-center mb-16">
            <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">Domain Expertise</h2>
            <h3 className="text-4xl font-bold text-[#1e3a8a]">Tailored Billing for Every Specialty</h3>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { t: "Cardiology", d: "Expert coding for complex cardiac procedures and imaging tests.", i: <HeartPulse size={32} /> },
            { t: "Mental Health", d: "Specialized billing for behavioral health and psychiatric services.", i: <Brain size={32} /> },
            { t: "Orthopedics", d: "Accurate CPT coding for surgeries and physical therapy recovery.", i: <Bone size={32} /> },
            { t: "Pediatrics", d: "Streamlined RCM for high-volume clinics and preventative care.", i: <Baby size={32} /> },
            { t: "Internal Medicine", d: "Comprehensive billing for acute and chronic patient management.", i: <Stethoscope size={32} /> },
            { t: "General Surgery", d: "Optimized revenue cycles for inpatient and outpatient surgical centers.", i: <Activity size={32} /> }
          ].map((spec, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="p-10 rounded-[2.5rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 group">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-[#14b8a6] mb-6 group-hover:bg-[#1e3a8a] group-hover:text-white transition-all duration-300">
                  {spec.i}
                </div>
                <h4 className="text-2xl font-bold text-[#1e3a8a] mb-4">{spec.t}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{spec.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* 3. DUAL-SERVICE MODEL DETAILS */}
      <section className="py-24 px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto space-y-32">
          
          {/* Medical Billing Section */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn direction="right">
              <div className="order-2 lg:order-1">
                <h3 className="text-4xl font-bold text-[#1e3a8a] mb-6">Medical Billing & RCM</h3>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  We handle the entire revenue cycle—from patient enrollment to payment posting—ensuring 
                  a 98% clean claim rate and faster reimbursements.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {["ICD-10/CPT Coding", "Denial Management", "Payment Posting", "A/R Recovery"].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-sm border-l-4 border-[#14b8a6]">
                      <CheckCircle2 className="text-[#14b8a6]" size={20} />
                      <span className="font-bold text-[#1e3a8a] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
            <FadeIn direction="left" delay={0.2}>
              <div className="order-1 lg:order-2 rounded-[3rem] overflow-hidden shadow-2xl">
                <img src={billingImg} alt="RCM Dashboard" className="w-full h-full object-cover" />
              </div>
            </FadeIn>
          </div>

          {/* Healthcare IT Section */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn direction="right" delay={0.2}>
              <div className="rounded-[3rem] overflow-hidden shadow-2xl">
                <img src={itImg} alt="Healthcare IT Network" className="w-full h-full object-cover" />
              </div>
            </FadeIn>
            <FadeIn direction="left">
              <div>
                <h3 className="text-4xl font-bold text-[#1e3a8a] mb-6">Healthcare IT Solutions</h3>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Our IT infrastructure is designed specifically for healthcare interoperability, 
                  maintaining rigorous HIPAA and HITECH security standards.
                </p>
                <div className="space-y-4">
                  {[
                    { t: "EHR/EMR Integration", icon: <Database /> },
                    { t: "HIPAA-Managed Security", icon: <ShieldCheck /> },
                    { t: "Real-time Analytics", icon: <BarChart3 /> }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-6 bg-white border border-slate-100 rounded-[2rem] hover:shadow-lg transition-all group">
                      <div className="text-[#14b8a6] group-hover:scale-110 transition-transform">{item.icon}</div>
                      <h4 className="font-bold text-[#1e3a8a]">{item.t}</h4>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>

        </div>
      </section>

      {/* 4. PERFORMANCE METRICS */}
      <section className="bg-[#1e3a8a] py-20 px-8 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 text-center">
            {[
              { label: "Revenue Growth", value: "+30%" },
              { label: "Claim Approvals", value: "+60%" },
              { label: "IT Cost Reduction", value: "70%" },
              { label: "Clean Claim Rate", value: "98%" }
            ].map((stat, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <p className="text-5xl font-black text-[#14b8a6] mb-2">{stat.value}</p>
                <p className="text-sm text-slate-400 uppercase tracking-widest font-bold">{stat.label}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 5. FINAL CALL TO ACTION */}
      <section className="py-32 px-8 text-center bg-white">
        <FadeIn direction="up">
          <div className="max-w-3xl mx-auto bg-slate-50 p-16 rounded-[4rem] border border-slate-100 shadow-xl">
            <h2 className="text-3xl md:text-5xl font-bold text-[#1e3a8a] mb-8 leading-tight">
              Modernize Your Practice Infrastructure Today.
            </h2>
            <Link 
              to="/contact" 
              className="bg-[#14b8a6] text-white px-12 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl transition-all inline-flex items-center gap-3 active:scale-95"
            >
              Get Your Free Revenue Audit <ChevronRight size={20} />
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default Services;